import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Box, Typography, Button, IconButton } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DraggableAppModal from '../Modals/DraggableAppModal';
import AddFeedbackDialog from './AddFeedbackDialog';
import FeedbackHistoryDialog from './FeedbackHistoryDialog';
import FilterModal from '../DashboardModal/components/ProjectView/cards/FilterModal';
import { DESERT_FLOWER, PEANUT_BUTTER_ORANGE, COOL_GREEN, DARK_GREY } from '../DashboardModal/dashboardColors';
import './AutoRecommendedAlertsPanel.scss';

// Color/filter by the resolved priority label text rather than the numeric enum value,
// mirroring the operational panel — robust to the enum numbering and label casing (e.g. "Med").
const getPriorityColor = (label) => {
  const t = (label || '').toLowerCase();
  if (t.includes('high')) return DESERT_FLOWER;        // red
  if (t.includes('med')) return PEANUT_BUTTER_ORANGE;  // orange
  if (t.includes('low')) return COOL_GREEN;            // green
  return DARK_GREY;
};

// 'medium' chip maps to the 'Med' label; high/low match directly.
const matchesPriorityFilter = (label, filters) => {
  if (!filters.length) return true;
  const t = (label || '').toLowerCase();
  return filters.some(f => t.includes(f === 'medium' ? 'med' : f));
};

const AutoRecommendedAlertsPanel = ({
  open,
  onClose,
  alerts = [],
  loading = false,
  highlightedAlertId,
  eventStateOptions = [],
  priorityOptions = [],
  alertStatusOptions = [],
  history = { data: [], loading: false },
  onAlertClick,
  onChangeState,
  onChangePriority,
  onAddFeedback,
  onFetchHistory,
}, context) => {
  const [feedbackDialogAlert, setFeedbackDialogAlert] = useState(null);
  const [historyAlertId, setHistoryAlertId] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [eventStateFilter, setEventStateFilter] = useState([]);
  const [alertStatusFilter, setAlertStatusFilter] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [feedbackDialogStyle, setFeedbackDialogStyle] = useState(null);
  const contentRef = useRef(null);
  const highlightedRowRef = useRef(null);

  // Keep the panel fully in view while resizing. The paper is top-anchored
  // (see .ara-resizable-paper), so a native vertical resize only grows it
  // downward — cap max-height at the space between its top edge and the bottom
  // of the viewport. Recomputed on mousedown so the cap reflects the panel's
  // current position at the moment a resize drag starts (the browser reads the
  // constraint when the drag begins, not mid-drag).
  useEffect(() => {
    if (!open) return undefined;
    const paper = contentRef.current && contentRef.current.closest('.ara-resizable-paper');
    if (!paper) return undefined;
    const clampHeight = () => {
      const fit = window.innerHeight - paper.getBoundingClientRect().top - 8;
      paper.style.maxHeight = `${Math.max(220, Math.floor(fit))}px`;
    };
    const observer = new ResizeObserver(clampHeight);
    observer.observe(paper);
    paper.addEventListener('mousedown', clampHeight, true);
    window.addEventListener('resize', clampHeight);
    clampHeight();
    return () => {
      observer.disconnect();
      paper.removeEventListener('mousedown', clampHeight, true);
      window.removeEventListener('resize', clampHeight);
    };
  }, [open]);

  // Close on Escape — topmost first: filter dialog > sub-dialogs > the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (filterDialogOpen) setFilterDialogOpen(false);
      else if (feedbackDialogAlert) setFeedbackDialogAlert(null);
      else if (historyAlertId != null) setHistoryAlertId(null);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, filterDialogOpen, feedbackDialogAlert, historyAlertId, onClose]);

  const openHistory = (alertId) => {
    setHistoryAlertId(alertId);
    onFetchHistory(alertId);
  };

  const handleStateChange = (alert, newValue) => {
    // Hold every state change until the feedback dialog is applied so the
    // backend receives the new state and comment together. Cancelling leaves
    // the controlled select on the existing value.
    setFeedbackDialogAlert({ ...alert, pendingEventState: newValue });
  };

  const priorityLabelOf = (a) => {
    const opt = priorityOptions.find(o => String(o.value) === String(a.priorityValue));
    return opt ? opt.label : '';
  };

  const visibleAlerts = alerts.filter((a) => {
    if (!matchesPriorityFilter(priorityLabelOf(a), priorityFilter)) return false;
    if (eventStateFilter.length > 0 && !eventStateFilter.some(v => String(v) === String(a.eventStateValue))) {
      return false;
    }
    if (alertStatusFilter.length > 0 && !alertStatusFilter.some(v => String(v) === String(a.alertStatusValue))) {
      return false;
    }
    return true;
  });

  if (sortConfig.key) {
    visibleAlerts.sort((a, b) => {
      let aValue;
      let bValue;
      switch (sortConfig.key) {
        case 'alertId': {
          // Alert ids are numeric — compare as numbers so 9 sorts before 10.
          const an = Number(a.alertId);
          const bn = Number(b.alertId);
          aValue = Number.isNaN(an) ? String(a.alertId || '') : an;
          bValue = Number.isNaN(bn) ? String(b.alertId || '') : bn;
          break;
        }
        case 'priority':
          aValue = a.priorityValue ?? 999;
          bValue = b.priorityValue ?? 999;
          break;
        case 'alertStatus':
          aValue = (a.alertStatus || '').toLowerCase();
          bValue = (b.alertStatus || '').toLowerCase();
          break;
        case 'ranking':
          aValue = a.ranking === '' ? -1 : a.ranking;
          bValue = b.ranking === '' ? -1 : b.ranking;
          break;
        case 'probability':
          aValue = a.probability || 0;
          bValue = b.probability || 0;
          break;
        case 'eventState':
          aValue = a.eventStateValue ?? 0;
          bValue = b.eventStateValue ?? 0;
          break;
        case 'comment':
          aValue = (a.comment || '').toLowerCase();
          bValue = (b.comment || '').toLowerCase();
          break;
        case 'feedback':
          aValue = (a.feedback || '').toLowerCase();
          bValue = (b.feedback || '').toLowerCase();
          break;
        default:
          return 0;
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const handleSort = (columnKey) => {
    setSortConfig(prev => (prev.key === columnKey
      ? { key: columnKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key: columnKey, direction: 'asc' }));
  };

  const highlightedAlertSnapshot = highlightedAlertId == null
    ? null
    : visibleAlerts.find(a => String(a.alertId) === String(highlightedAlertId));

  useEffect(() => {
    if (!open || highlightedAlertId == null || !highlightedRowRef.current) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      highlightedRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [
    open,
    loading,
    highlightedAlertId,
    highlightedAlertSnapshot,
    priorityFilter,
    eventStateFilter,
    alertStatusFilter,
    sortConfig,
  ]);

  useEffect(() => {
    if (!feedbackDialogAlert) {
      setFeedbackDialogStyle(null);
      return undefined;
    }

    const positionDialog = () => {
      const panel = contentRef.current && contentRef.current.closest('.ara-resizable-paper');
      if (!panel) return;

      const rect = panel.getBoundingClientRect();
      const gap = 4;
      const minMargin = 16;
      const nextTop = Math.min(
        Math.max(minMargin, rect.bottom + gap),
        window.innerHeight - 260
      );
      const nextLeft = Math.min(
        Math.max(minMargin, rect.left),
        window.innerWidth - rect.width - minMargin
      );

      setFeedbackDialogStyle({
        position: 'fixed',
        margin: 0,
        top: `${Math.max(minMargin, nextTop)}px`,
        left: `${Math.max(minMargin, nextLeft)}px`,
        width: `${Math.max(320, rect.width)}px`,
      });
    };

    const frameId = window.requestAnimationFrame(positionDialog);
    window.addEventListener('resize', positionDialog);
    window.addEventListener('scroll', positionDialog, true);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', positionDialog);
      window.removeEventListener('scroll', positionDialog, true);
    };
  }, [feedbackDialogAlert]);

  const renderSortableHeader = (columnKey, label) => {
    const isSorted = sortConfig.key === columnKey;
    return (
      <th
        className={`sortable-header ${isSorted ? 'sorted' : ''}`}
        onClick={() => handleSort(columnKey)}
      >
        <span className="header-content">
          {label}
          <span className={`sort-icon ${isSorted ? 'active' : ''}`}>
            {isSorted && sortConfig.direction === 'desc' ? (
              <ArrowDownwardIcon fontSize="small" />
            ) : (
              <ArrowUpwardIcon fontSize="small" />
            )}
          </span>
        </span>
      </th>
    );
  };

  const activeFilterCount = priorityFilter.length + eventStateFilter.length + alertStatusFilter.length;

  const content = (
    <Box className="auto-recommended-alerts-panel" ref={contentRef}>
      {loading && <Typography className="ara-status">{context.t('loading')}</Typography>}
      {!loading && visibleAlerts.length === 0 && (
        <Typography className="ara-status">{context.t('auto_recommended_alerts')}: 0</Typography>
      )}
      {!loading && visibleAlerts.length > 0 && (
        <div className="ara-table-container">
          <table className="ara-table">
            <thead>
              <tr>
                {renderSortableHeader('alertId', context.t('alert_id'))}
                {renderSortableHeader('priority', context.t('event_priority'))}
                {renderSortableHeader('alertStatus', context.t('alert_status'))}
                {renderSortableHeader('ranking', context.t('ranking'))}
                {renderSortableHeader('probability', context.t('probability'))}
                {renderSortableHeader('eventState', context.t('event_state'))}
                {renderSortableHeader('comment', context.t('comment'))}
                {renderSortableHeader('feedback', context.t('feedback'))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleAlerts.map((a) => {
                const isHighlighted = highlightedAlertId != null && String(highlightedAlertId) === String(a.alertId);
                return (
                  <tr
                    key={a.alertId}
                    className={`ara-row ${isHighlighted ? 'highlighted' : ''}`}
                    onClick={() => onAlertClick(a.alertId)}
                    ref={isHighlighted ? highlightedRowRef : null}
                  >
                    <td><span className="ara-id">{a.alertId}</span></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        className="ara-priority-select"
                        style={{ color: getPriorityColor(priorityLabelOf(a)) }}
                        value={a.priorityValue != null ? String(a.priorityValue) : ''}
                        onChange={(e) => onChangePriority(a.alertId, e.target.value)}
                      >
                        {priorityOptions.map(o => (
                          <option key={o.value} value={String(o.value)}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td><span className="ara-status-text">{a.alertStatus}</span></td>
                    <td><span className="ara-rank">{a.ranking}</span></td>
                    <td><span className="ara-prob">{a.probability}</span></td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        className="ara-state-select"
                        value={a.eventStateValue != null ? String(a.eventStateValue) : ''}
                        onChange={(e) => handleStateChange(a, e.target.value)}
                      >
                        {eventStateOptions.map(o => (
                          <option key={o.value} value={String(o.value)}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="ara-ellipsis" title={a.comment || ''}>{a.comment || ''}</td>
                    <td className="ara-ellipsis" title={a.feedback || ''}>{a.feedback || ''}</td>
                    <td className="ara-actions" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        className="ara-btn ara-btn-feedback"
                        onClick={() => setFeedbackDialogAlert(a)}
                      >
                        {context.t('add_feedback')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        className="ara-btn ara-btn-history"
                        onClick={() => openHistory(a.alertId)}
                      >
                        {context.t('history')}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {feedbackDialogAlert && (
        <AddFeedbackDialog
          alert={feedbackDialogAlert}
          paperStyle={feedbackDialogStyle}
          onCancel={() => setFeedbackDialogAlert(null)}
          onApply={(feedback) => {
            if (feedbackDialogAlert.pendingEventState != null) {
              onChangeState(feedbackDialogAlert.alertId, feedbackDialogAlert.pendingEventState, feedback);
            } else {
              onAddFeedback(feedbackDialogAlert.alertId, feedback);
            }
            setFeedbackDialogAlert(null);
          }}
        />
      )}

      {historyAlertId != null && (
        <FeedbackHistoryDialog
          alertId={historyAlertId}
          history={history}
          onClose={() => setHistoryAlertId(null)}
        />
      )}

      {/* Portal to body so the fixed-position overlay escapes the draggable
          panel's CSS transform and fills the viewport at a constant size. */}
      {ReactDOM.createPortal(
        <FilterModal
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          currentFilter={priorityFilter}
          currentEventStateFilter={eventStateFilter}
          eventStateOptions={eventStateOptions}
          currentAlertStatusFilter={alertStatusFilter}
          alertStatusOptions={alertStatusOptions}
          onSave={({ priorities, eventStates, alertStatuses }) => {
            setPriorityFilter(priorities);
            setEventStateFilter(eventStates);
            setAlertStatusFilter(alertStatuses || []);
          }}
          t={context.t}
        />,
        document.body
      )}
    </Box>
  );

  return (
    <DraggableAppModal
      open={open}
      onClose={onClose}
      title={context.t('auto_recommended_alerts')}
      titleClassName="draggable-blue-header"
      paperClassName="ara-resizable-paper"
      dragBounds="parent"
      headerActions={
        <IconButton
          onClick={() => setFilterDialogOpen(true)}
          title={context.t('filters')}
          sx={{ position: 'relative' }}
        >
          <FilterListIcon />
          {activeFilterCount > 0 && (
            <span style={{
              position: 'absolute', top: 4, right: 4,
              background: '#fff', color: '#0A3696',
              borderRadius: '50%', minWidth: 15, height: 15,
              fontSize: 10, fontWeight: 700, lineHeight: '15px', textAlign: 'center',
            }}>{activeFilterCount}</span>
          )}
        </IconButton>
      }
      hideBackdrop={true}
      closeIcon={true}
      maxWidth="xl"
      content={content}
    />
  );
};

AutoRecommendedAlertsPanel.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  alerts: PropTypes.array,
  loading: PropTypes.bool,
  highlightedAlertId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  eventStateOptions: PropTypes.array,
  priorityOptions: PropTypes.array,
  alertStatusOptions: PropTypes.array,
  history: PropTypes.object,
  onAlertClick: PropTypes.func.isRequired,
  onChangeState: PropTypes.func.isRequired,
  onChangePriority: PropTypes.func.isRequired,
  onAddFeedback: PropTypes.func.isRequired,
  onFetchHistory: PropTypes.func.isRequired,
};

AutoRecommendedAlertsPanel.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default AutoRecommendedAlertsPanel;
